import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container } from '@mui/material';
import { AlertCircle, Home as HomeIcon, ArrowRight } from 'lucide-react';

const Error404 = ({ isPermissionDenied = false }) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '400px',
          height: '400px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          top: '-100px',
          right: '-100px'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '300px',
          height: '300px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          bottom: '-50px',
          left: '-50px'
        }
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            padding: { xs: 3, sm: 5 },
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.6s ease-out'
          }}
        >
          {/* Large Error Icon/Number */}
          <Box
            sx={{
              fontSize: { xs: '120px', sm: '150px' },
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 20px 0',
              lineHeight: 1
            }}
          >
            {isPermissionDenied ? <AlertCircle size={120} style={{ display: 'inline' }} /> : '404'}
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: 2,
              fontSize: { xs: '24px', sm: '32px' }
            }}
          >
            {isPermissionDenied ? 'Access Denied' : 'Page Not Found'}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontSize: { xs: '14px', sm: '16px' },
              lineHeight: 1.6,
              mb: 4
            }}
          >
            {isPermissionDenied
              ? "You don't have permission to access this page. Please contact your administrator if you believe this is an error."
              : "Sorry, the page you're looking for doesn't exist. It might have been moved or deleted."}
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            <Button
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                px: 3,
                py: 1.2,
                fontSize: '15px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)'
                },
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1
              }}
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon size={18} />
              Go to Dashboard
            </Button>

            <Button
              variant="outlined"
              sx={{
                color: '#667eea',
                borderColor: '#667eea',
                px: 3,
                py: 1.2,
                fontSize: '15px',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#764ba2',
                  color: '#764ba2',
                  backgroundColor: 'rgba(102, 126, 234, 0.05)'
                },
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1
              }}
              onClick={() => navigate(-1)}
            >
              <ArrowRight size={18} />
              Go Back
            </Button>
          </Box>
        </Box>

        {/* Decorative Text */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(255, 255, 255, 0.7)',
            mt: 4,
            fontSize: '12px'
          }}
        >
          Error Code: {isPermissionDenied ? '403' : '404'}
        </Typography>
      </Container>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Box>
  );
};

export default Error404;
